import { Injectable } from '@angular/core';
import { DistillingService } from '../distilling-service/distilling-service';

/**
 * The narrowing source a picker is reading from.
 *
 * - `plan` narrows to what the project's own sampling plan names for the selected line items.
 * - `spec` widens to everything the contract's specification set allows for those line items'
 *   pay items, unioned with the plan.
 * - `all` lifts narrowing altogether.
 */
export type SpecScopeMode = 'plan' | 'spec' | 'all';

/** The key the bundle's mode override map uses for the widened source. */
const FULL_SPEC_MODE_KEY = 'FullSpec';

/**
 * Resolves which narrowing source the material and test pickers read from, and hands back the
 * Grafee filter overrides that express it.
 *
 * Both sources live in the distilling bundle: the project's sampling plan
 * (ProjectLineItemMaterialTest) and the specification set's template for each pay item
 * (PayItemMaterialTestRequirement). They sit at the same grain, so choosing between them is a
 * matter of which filter paths a picker walks, not of what it selects.
 *
 * The plan is the default and falls through to the template when the project has no plan entries
 * for the line items in hand. Widening is never allowed to shorten a list: the widened override
 * map unions the plan path into every group, so `spec` is a superset of `plan` by construction.
 *
 * Item-side scope is independent — the Contract carries MaterialIDTestSpecificationSet and
 * ItemIDTestSpecificationSet separately — and this app does not pick pay items, so the item-side
 * accessors exist for diagnostics.
 *
 * Every accessor fails open: no graph, no bundle settings, or a bundle that cannot traverse to
 * anything returns null and the caller leaves the picker unnarrowed.
 */
@Injectable()
export class SpecScopeProvider
{
	private lineItemGraph: any = null;
	private lineItemIDs: Array<any> = null;
	// resolveMode is reached from getActiveSelections, so it is asked once per render of every
	// picker. Both answers are properties of the bundle, so they cannot change without the graph
	// being replaced - which is exactly what keys the cache.
	private entryGraph: any = null;
	private entryCache: { [key: string]: boolean } = {};

	constructor(private distilling: DistillingService) {}

	/**
	 * Resolve the mode a picker should actually use, applying the plan-to-template fall-through.
	 *
	 * @param {SpecScopeMode} pRequestedMode The mode the user selected.
	 * @param {Array<number>} pLineItems The line items currently selected on the sample.
	 * @return {SpecScopeMode} The mode to filter with.
	 */
	resolveMode(pRequestedMode: SpecScopeMode, pLineItems: Array<number>): SpecScopeMode
	{
		if (pRequestedMode !== 'plan')
		{
			return pRequestedMode;
		}
		if (this.hasPlanEntries(pLineItems))
		{
			return 'plan';
		}
		if (this.hasTemplateEntries())
		{
			return 'spec';
		}
		// The project has a plan, just not for these line items: an empty plan tab is the honest
		// answer. A project with no plan and no specification template has nothing to narrow on at
		// all, and a narrowed tab there is an empty list with a filter nobody can satisfy.
		return this.hasPlanEntries([]) ? 'plan' : 'all';
	}

	/**
	 * Whether the narrowing control can do anything for this project.
	 *
	 * False when neither the sampling plan nor the specification template has a single entry -
	 * every narrowed position would be empty, so the control is hidden and the picker says why.
	 *
	 * @return {boolean}
	 */
	hasAnyNarrowingSource(): boolean
	{
		return this.hasPlanEntries([]) || this.hasTemplateEntries();
	}

	/**
	 * The Grafee `requestFilterOverrides` array for an entity type under a given mode.
	 *
	 * `plan` and `all` return null, which leaves Grafee on the bundle's own FilterOverrides — the
	 * plan paths. Only the widened mode replaces them.
	 *
	 * @param {SpecScopeMode} pMode The active mode.
	 * @param {string} pEntityType The entity type being listed (e.g. 'IDMaterial').
	 * @return {Array<any>} The overrides, or null to use the bundle's defaults.
	 */
	getFilterOverrides(pMode: SpecScopeMode, pEntityType: string): Array<any>
	{
		if (pMode !== 'spec')
		{
			return null;
		}
		const modeMap = this.distilling.bundleSettings && this.distilling.bundleSettings.FilterOverrideModes;
		const modeOverrides = modeMap && modeMap[FULL_SPEC_MODE_KEY] && modeMap[FULL_SPEC_MODE_KEY][pEntityType];
		return Array.isArray(modeOverrides) && modeOverrides.length ? modeOverrides : null;
	}

	/**
	 * Every line item on the project.
	 *
	 * The bundle is built per project, so its whole line-item set is the project's.
	 *
	 * Memoised against the graph instance: this is read on every selection rebuild, and the answer
	 * cannot change without the bundle being replaced.
	 *
	 * @return {Array<any>} Line item ids, empty when the bundle carries none.
	 */
	getProjectLineItemIDs(): Array<any>
	{
		const graph = this.distilling.graph;
		if (!graph)
		{
			return [];
		}
		if (this.lineItemGraph === graph && this.lineItemIDs)
		{
			return this.lineItemIDs;
		}
		this.lineItemGraph = graph;
		this.lineItemIDs = this.safeListValues('IDLineItem', {});
		return this.lineItemIDs;
	}

	/**
	 * A project-wide anchor, for pickers that would otherwise be unnarrowed.
	 *
	 * Every narrowing path starts at a line item, so before one is chosen nothing engages and the
	 * picker shows the whole catalogue - in the widened mode as much as the default one, which
	 * makes "everything the spec allows" indistinguishable from "everything".
	 *
	 * Anchoring on ALL the project's line items answers the question the user is really asking:
	 * what does this project call for, across everything in it. Because it is the same anchor the
	 * per-line-item paths use, the mode's own overrides apply on top for free - the default paths
	 * give the plan, the widened ones give plan united with the template.
	 *
	 * Returns null once line items are selected, since the real anchor is strictly better, and in
	 * `all`, which means no narrowing at all.
	 *
	 * @param {SpecScopeMode} pMode The effective mode.
	 * @param {any} pSelections The picker's current selections.
	 * @return {object} A selection chunk, or null to leave the picker as it is.
	 */
	getProjectScopeAnchor(pMode: SpecScopeMode, pSelections: any): any
	{
		if (pMode === 'all')
		{
			return null;
		}
		if (pSelections && pSelections.IDLineItem && pSelections.IDLineItem.length)
		{
			return null;
		}
		const lineItems = this.getProjectLineItemIDs();
		return lineItems.length ? { IDLineItem: lineItems } : null;
	}

	/**
	 * Whether the bundle carries sampling-plan entries for the given line items.
	 *
	 * @param {Array<number>} pLineItems The selected line items.
	 * @return {boolean} True when the plan can narrow.
	 */
	hasPlanEntries(pLineItems: Array<number>): boolean
	{
		const tmpKey = `plan:${ (pLineItems || []).join(',') }`;
		return this.cachedEntryCheck(tmpKey, () => (!pLineItems || !pLineItems.length)
			? this.safeListValues('IDProjectLineItemMaterialTest', {}).length > 0
			: this.safeListValues('IDProjectLineItemMaterialTest', { IDLineItem: pLineItems }).length > 0);
	}

	/**
	 * Answer a bundle-level yes/no once per bundle.
	 *
	 * @param {string} pKey The question.
	 * @param {Function} pResolve How to answer it.
	 * @return {boolean} The answer.
	 */
	private cachedEntryCheck(pKey: string, pResolve: () => boolean): boolean
	{
		const tmpGraph = this.distilling.graph;
		if (this.entryGraph !== tmpGraph)
		{
			this.entryGraph = tmpGraph;
			this.entryCache = {};
		}
		if (!(pKey in this.entryCache))
		{
			this.entryCache[pKey] = pResolve();
		}
		return this.entryCache[pKey];
	}

	/**
	 * Whether the bundle carries specification-set requirement rows at all. A contract naming no
	 * set, or naming a set with no requirements, loads none — and widening has nothing to widen to.
	 *
	 * @return {boolean} True when the template can narrow.
	 */
	hasTemplateEntries(): boolean
	{
		return this.cachedEntryCheck('template', () => this.safeListValues('IDPayItemMaterialTestRequirement', {}).length > 0);
	}

	/**
	 * The contract's material-side specification set ids.
	 *
	 * Still a real scope even though it no longer filters pickers — LabTestSpecificationSetJoin
	 * decides which generation of field lab a project may use, and the lab picker reads it from
	 * here rather than from a selection key.
	 *
	 * @return {Array<number>} The set ids, empty when the contract names none.
	 */
	getActiveMaterialScopeIDs(): Array<number>
	{
		return this.safeListValues('IDTestSpecificationSet', {});
	}

	/**
	 * The contract's item-side specification set ids, for diagnostics and pay-item consumers.
	 *
	 * @return {Array<number>} The set ids, empty when the contract names none.
	 */
	getActiveItemScopeIDs(): Array<number>
	{
		return this.safeListValues('IDItemTestSpecificationSet', {});
	}

	/**
	 * The item-side selection chunk for callers that narrow pay-item pickers. Not used by this app
	 * today — sample tracking does not pick pay items.
	 *
	 * @return {object} The chunk, or null when the contract's item-side FK is unset.
	 */
	getActiveItemScopeSelection(): { IDItemTestSpecificationSet: number[] } | null
	{
		const itemSets = this.getActiveItemScopeIDs();
		return itemSets.length ? { IDItemTestSpecificationSet: itemSets } : null;
	}

	private safeListValues(entityType: string, selections: any): any[]
	{
		const graph = this.distilling.graph;
		if (!graph)
		{
			return [];
		}
		try
		{
			const values = graph.listValues(entityType, selections);
			return Array.isArray(values) ? values : [];
		}
		catch (err)
		{
			return [];
		}
	}
}



// WEBPACK FOOTER //
// ./src/providers/spec-scope-service/spec-scope.service.ts